/* eslint-disable react-hooks/exhaustive-deps */
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { getProfilePics } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getMeetingInfo } from '@vantiva-sa/homesight_humaui/Store/videoCallStore';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { noop } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useWS, { ReadyState } from 'react-use-websocket';
import { useMainContext } from '../../MainContext';
import useUserInfo from '../Hooks/useUserInfo';
import { sendAttendeeInfo, sendCallInfo, sendMetricsInfo } from './TelemetryUtils';
import { checkForMaintenance, processMessage } from './WebsocketHelper';

const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars);
const WEBSOCKET_URL = ENV_VARS.WEBSOCKET_URL;
const WEBSOCKET_PORT = ENV_VARS.WEBSOCKET_PORT;

const useWebsocket = (hostname, token, setAnnouncement) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleMainMenuClick, navigationGuard } = useMainContext();
  const [connect, setConnect] = useState(false);
  const currentUser = useSelector(getCurrentUser);
  const resourceMap = useSelector(getProfilePics());
  const meetingInfo = useSelector(getMeetingInfo);
  const videoCallModalVisible = useSelector((state) => state.videoCall.videoCallModalVisible);
  const videoCallNotificationModalVisible = useSelector(
    (state) => state.notification.videoCallNotificationModalVisible
  );

  const useruuid = useMemo(() => currentUser.useruuid, [currentUser]);
  const WS_URL = useMemo(
    () => (WEBSOCKET_URL || 'wss://' + hostname) + ':' + WEBSOCKET_PORT,
    [hostname]
  );

  const { data: userInfo = {} } = useUserInfo({ useruuid: currentUser.useruuid });

  /* Notifications are clicked long after they are raised, so navigation must be resolved then, not now. */
  const navigationRef = useRef({});
  useEffect(() => {
    navigationRef.current = { navigate, handleMainMenuClick, navigationGuard };
  });

  const maintenanceApi = useSelector((state) => state.apis.maintenanceApi);
  const trans = useSelector((state) => state.translator);

  const onMessage = async (e) => {
    try {
      const message = JSON.parse(e.data) || {};
      if (message.type === 'pong') {
        // Handle ping response if needed
        noop();
      } else if (message.type === 'check_for_maintenance') {
        checkForMaintenance(userInfo, setAnnouncement, maintenanceApi, trans);
      } else {
        processMessage({
          dispatch,
          message,
          sendMessage,
          meetingInfo,
          videoCallModalVisible,
          videoCallNotificationModalVisible,
          currentUser,
          resourceMap,
          navigationRef,
        });
      }
    } catch (error) {
      console.error('Error parsing websocket message:', error);
    }
  };

  useEffect(() => setConnect(false), [token]);

  useEffect(() => {
    if (connect === false) {
      setConnect(true);
    }
  }, [connect]);

  const { sendMessage: sendMessageToWS, readyState } = useWS(
    WS_URL,
    {
      protocols: token,
      shouldReconnect: (closeEvent) => {
        return closeEvent.code !== 1000 && closeEvent.code !== 1001;
      },
      reconnectInterval: 5000 /* 5 sec */,
      reconnectAttempts: Infinity,
      heartbeat: {
        message: JSON.stringify({ type: 'ping' }),
        returnMessage: JSON.stringify({ type: 'pong' }),
        interval: 120 * 1000 /* 120 sec */,
        timeout: 250 * 1000 /* 250 sec */,
      },
      onOpen: (e) => {
        // Handle successful connection if needed
      },
      onClose: (e) => {
        // Handle connection close if needed
      },
      onError: (e) => {
        console.error('Websocket connection error:', e);
      },
      onMessage: onMessage,
    },
    connect
  );

  const sendMessage = (message, type = 'message', topic) => {
    /* ToDo: message to be zipped and encrypted */
    const msgObject = { type, id: useruuid, message, topic };
    if (readyState !== ReadyState.OPEN) {
      console.error("Websocket connection is not open, can't send message!", readyState);
      return;
    }
    sendMessageToWS(JSON.stringify(msgObject));
  };
  return {
    sendMessage,
    sendMetricsInfo: (audioVideo) => sendMetricsInfo(audioVideo, sendMessage),
    sendAttendeeInfo: (attendeeInfo) => sendAttendeeInfo(sendMessage, attendeeInfo),
    sendCallInfo: (audioVideo) => sendCallInfo(audioVideo, sendMessage),
    readyState,
  };
};

export default useWebsocket;
